// src/components/ui/ProgressIndicator.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
  showSteps?: boolean;
  showPercentage?: boolean;
  variant?: 'default' | 'gradient' | 'steps';
  className?: string;
}

export function ProgressIndicator({ 
  currentStep, 
  totalSteps,
  showSteps = true,
  showPercentage = true,
  variant = 'default',
  className
}: ProgressIndicatorProps) {
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const variants = {
    default: 'bg-primary-600',
    gradient: 'bg-gradient-to-r from-primary-500 to-primary-600',
    steps: 'bg-primary-600'
  };

  return (
    <div className={cn('relative pt-1', className)}>
      {(showSteps || showPercentage) && (
        <div className="flex mb-2 items-center justify-between">
          {showSteps && (
            <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-primary-600 bg-primary-200">
              Step {currentStep + 1} of {totalSteps}
            </span>
          )}
          {showPercentage && (
            <span className="text-xs font-semibold inline-block text-primary-600">
              {Math.round(progress)}%
            </span>
          )}
        </div>
      )}
      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded-full bg-primary-100">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className={cn(
            'shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center',
            variants[variant]
          )}
        />
      </div>
    </div>
  );
}